// JWT Token utility functions

export interface JWTPayload {
  exp: number;
  iat: number;
  sub?: string;
  user_id?: string;
  id?: string;
  [key: string]: unknown;
}

/**
 * Decode JWT token without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    if (!payload) {
      return null;
    }

    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param token - JWT token string
 * @returns true if expired, false if valid
 */
export const isTokenExpired = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return true;
  }

  try {
    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch {
    // If token parsing fails, consider it expired
    return true;
  }
};

/**
 * Check if token is valid (exists and not expired)
 * @param token - JWT token string
 * @returns true if valid, false otherwise
 */
export const isValidToken = (token: string): boolean => {
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    return false;
  }

  try {
    return !isTokenExpired(token);
  } catch {
    // If token parsing fails, consider it invalid
    return false;
  }
};

/**
 * Get time until token expires in seconds
 * @param token - JWT token string
 * @returns seconds until expiration, negative if expired
 */
export const getTimeUntilExpiry = (token: string): number => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return -1;
  }

  const currentTime = Date.now() / 1000;
  return payload.exp - currentTime;
};

/**
 * Check if token will expire within the specified time
 * @param token - JWT token string
 * @param bufferSeconds - Buffer time in seconds (default: 300 = 5 minutes)
 * @returns true if token will expire within buffer time
 */
export const isTokenExpiringSoon = (
  token: string,
  bufferSeconds: number = 300
): boolean => {
  const timeUntilExpiry = getTimeUntilExpiry(token);
  return timeUntilExpiry > 0 && timeUntilExpiry <= bufferSeconds;
};

/**
 * Extract user ID from JWT token
 * @param token - JWT token string
 * @returns user ID or null if not found
 */
export const getUserIdFromToken = (token: string): string | null => {
  const payload = decodeJWT(token);
  return payload?.sub || payload?.user_id || payload?.id || null;
};
